import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppBooking from '../components/WhatsAppBooking';
import SEOJsonLd from '../../../components/base/SEOJsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thesunshinehealing.com';

const studioImage = 'https://public.readdy.ai/ai/img_res/561a5639c2d8c3dc8e54df329358952e.jpg spiritual healing studio banner design with glowing lotus flowers chakra rainbow energy light sacred geometry golden patterns serene meditation space white and gold cream color palette peaceful divine atmosphere ethereal glow natural elements Singapore wellness brand beautiful harmonious spiritual landscape with soft light rays and healing crystals&width=1200&height=630&seq=studio-og-preview-001&orientation=landscape';

const oracleJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Oracle Card Reading Singapore — Divine Guidance & Intuitive Wisdom 神谕卡解读',
    description:
      'Oracle Card reading in Singapore. Receive divine guidance and intuitive insights for life decisions, relationships and spiritual growth. 15–30 min readings from SGD $40. Bilingual Chinese & English.',
    url: `${siteUrl}/services/oracle`,
    image: studioImage,
    areaServed: 'Singapore',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Sunshine Healing Studio',
      url: siteUrl,
      telephone: '+6593651286',
      address: { '@type': 'PostalAddress', addressLocality: 'Singapore', addressCountry: 'SG' },
    },
    offers: [
      { '@type': 'Offer', price: '40', priceCurrency: 'SGD', description: 'Single Topic Oracle Card Reading (15 mins)' },
      { '@type': 'Offer', price: '60', priceCurrency: 'SGD', description: 'In-Depth Oracle Card Reading (30 mins)' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页 Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: '神谕卡解读 Oracle Card Reading', item: `${siteUrl}/services/oracle` },
    ],
  },
];

export default function OraclePage() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-white">
      <title>神谕卡解读 新加坡 | Oracle Card Reading Singapore — Divine Guidance & Intuition</title>
      <meta name="description" content="新加坡神谕卡解读，借助神圣智慧与直觉引导，为当下生命议题带来清晰洞见与指引，连接高我智慧找到前行方向。15-30分钟，SGD $40起。Singapore oracle card reading — divine guidance, intuition, spiritual direction." />
      <meta name="keywords" content="oracle card reading Singapore, spiritual guidance Singapore, tarot oracle Singapore, intuitive reading, divine wisdom" />
      <link rel="canonical" href={`${siteUrl}/services/oracle`} />
      <meta property="og:title" content="神谕卡解读 新加坡 | Oracle Card Reading Singapore" />
      <meta property="og:description" content="神谕卡解读，连接高我智慧，为人生议题带来清晰指引。新加坡专业灵性疗愈师，中英双语服务。" />
      <meta property="og:url" content={`${siteUrl}/services/oracle`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={studioImage} />
      <SEOJsonLd id="oracle-jsonld" data={oracleJsonLd} />

      <Navbar />

      {/* Hero Banner */}
      <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center overflow-hidden pt-16 md:pt-0">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=mystical%20oracle%20cards%20spread%20on%20sacred%20altar%20with%20warm%20golden%20light%2C%20divine%20guidance%20visualization%2C%20spiritual%20intuition%20scene%2C%20ethereal%20magical%20atmosphere%2C%20celestial%20wisdom%20symbols%2C%20enchanting%20mystical%20environment%2C%20soft%20glowing%20sacred%20space%20with%20cosmic%20energy&width=1920&height=1080&seq=oracle-hero-bg&orientation=landscape"
            alt="Oracle Cards"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/65 via-stone-900/45 to-stone-900/65"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 md:px-6 text-center">
          <div className="inline-block px-4 py-1.5 bg-amber-500/20 backdrop-blur-sm rounded-full border border-amber-400/30 mb-4 md:mb-5">
            <span className="text-amber-300 text-xs md:text-sm font-medium tracking-widest">SPIRITUAL HEALING</span>
          </div>
          <h1 className="text-3xl md:text-6xl font-bold text-white mb-4 md:mb-5 tracking-tight">
            {t('oracleDetail.hero.title')}
          </h1>
          <p className="text-base md:text-xl text-stone-200 leading-relaxed">
            {t('oracleDetail.hero.description')}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 md:items-center gap-8">
            <div>
              <p className="text-amber-600 text-xs md:text-sm font-semibold tracking-widest uppercase mb-2 md:mb-3">Introduction</p>
              <h2 className="text-2xl md:text-4xl font-bold text-stone-800 mb-4 md:mb-6 leading-snug">
                {t('oracleDetail.intro.title')}
              </h2>
              <p className="text-stone-600 leading-relaxed text-sm md:text-base">
                {t('oracleDetail.intro.content')}
              </p>
              <div className="mt-6 md:mt-10 grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { icon: 'ri-eye-line', label: t('oracleDetail.benefits.benefit1.title') },
                  { icon: 'ri-compass-3-line', label: t('oracleDetail.benefits.benefit3.title') },
                  { icon: 'ri-flashlight-line', label: t('oracleDetail.benefits.benefit4.title') },
                  { icon: 'ri-seedling-line', label: t('oracleDetail.benefits.benefit5.title') },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 md:gap-3 bg-amber-50 rounded-xl px-3 md:px-4 py-2.5 md:py-3">
                    <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-amber-600 flex-shrink-0">
                      <i className={`${item.icon} text-base md:text-lg`}></i>
                    </div>
                    <span className="text-stone-700 text-xs md:text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative w-full h-64 md:h-[520px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20oracle%20cards%20being%20drawn%20with%20hands%2C%20mystical%20card%20reading%20scene%20with%20warm%20golden%20light%2C%20spiritual%20guidance%20visualization%2C%20enchanting%20magical%20atmosphere%2C%20divine%20wisdom%20symbols%2C%20serene%20sacred%20space%2C%20soft%20ethereal%20glow&width=800&height=1000&seq=oracle-intro-img&orientation=portrait"
                alt="Oracle Card Reading"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Suitable For & Method */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-amber-50/60">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:grid md:grid-cols-2 md:gap-16 gap-10">
            {/* 适合人群 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-user-heart-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('oracleDetail.suitable.title')}</h3>
              </div>
              <ul className="space-y-3">
                {[
                  t('oracleDetail.suitable.item1'),
                  t('oracleDetail.suitable.item2'),
                  t('oracleDetail.suitable.item3'),
                  t('oracleDetail.suitable.item4'),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-600 text-sm md:text-base">
                    <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-checkbox-circle-fill text-amber-500 text-base"></i>
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* 疗愈方式 */}
            <div>
              <div className="flex items-center gap-3 mb-5 md:mb-7">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
                  <i className="ri-magic-line text-lg"></i>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('oracleDetail.method.title')}</h3>
              </div>
              <div className="space-y-4">
                {[
                  { icon: 'ri-focus-2-line', title: t('oracleDetail.method.step1.title'), desc: t('oracleDetail.method.step1.desc') },
                  { icon: 'ri-layout-grid-line', title: t('oracleDetail.method.step2.title'), desc: t('oracleDetail.method.step2.desc') },
                  { icon: 'ri-lightbulb-flash-line', title: t('oracleDetail.method.step3.title'), desc: t('oracleDetail.method.step3.desc') },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-xl shadow-sm border border-amber-100">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 flex-shrink-0">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <p className="font-semibold text-stone-800 text-sm md:text-base mb-1">{item.title}</p>
                      <p className="text-stone-500 text-xs md:text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-12 md:py-20 px-4 md:px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100 text-amber-600">
              <i className="ri-price-tag-3-line text-lg"></i>
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-stone-800">{t('oracleDetail.pricing.title')}</h3>
          </div>
          <p className="text-stone-400 text-sm mb-8">{t('oracleDetail.pricing.note')}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {(t('oracleDetail.pricing.items', { returnObjects: true }) as { duration: string; price: string; label: string }[]).map((item, i) => (
              <div key={i} className="flex-1 max-w-xs mx-auto sm:mx-0 bg-amber-50 border border-amber-100 rounded-2xl px-8 py-7 flex flex-col items-center gap-2 shadow-sm">
                <span className="text-xs font-semibold tracking-widest text-amber-500 uppercase mb-1">{item.label}</span>
                <div className="flex items-center gap-2 text-stone-700">
                  <div className="w-5 h-5 flex items-center justify-center text-amber-400">
                    <i className="ri-time-line text-base"></i>
                  </div>
                  <span className="text-base font-medium">{item.duration}</span>
                </div>
                <div className="text-2xl md:text-3xl font-bold text-amber-600 mt-1">{item.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking */}
      <WhatsAppBooking accentColor="amber" serviceName="神谕卡" />

      {/* Back */}
      <section className="py-8 md:py-10 px-4 md:px-6 text-center bg-white">
        <Link
          to="/#services"
          className="inline-flex items-center gap-2 px-6 md:px-7 py-3 border border-stone-300 text-stone-700 rounded-full hover:bg-stone-100 transition-colors duration-300 text-sm whitespace-nowrap cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t('oracleDetail.cta.back')}</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
}
